/**
 * Zento AI Widget Styles
 * 
 * All CSS styles are embedded as JavaScript strings to keep the widget self-contained.
 * This allows the widget to work without external CSS dependencies.
 */

/** Full-viewport ticket PDF overlay (mounted on document.body, outside Shadow DOM). */
export const createTicketPdfOverlayStyles = () => `
    .zento-ticket-pdf-overlay {
      position: fixed !important;
      inset: 0 !important;
      z-index: 2147483647 !important;
      display: none !important;
      align-items: stretch !important;
      justify-content: stretch !important;
      background: #0f172a !important;
      padding: 0 !important;
      margin: 0 !important;
      box-sizing: border-box !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      line-height: 1.5 !important;
    }

    .zento-ticket-pdf-overlay-visible {
      display: flex !important;
    }

    .zento-ticket-pdf-dialog {
      width: 100vw !important;
      height: 100vh !important;
      max-width: 100vw !important;
      max-height: 100vh !important;
      background: white !important;
      border-radius: 0 !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
      box-shadow: none !important;
      box-sizing: border-box !important;
    }

    .zento-ticket-pdf-header {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 12px !important;
      padding: 14px 16px !important;
      border-bottom: 1px solid #e2e8f0 !important;
      flex-shrink: 0 !important;
      background: white !important;
    }

    .zento-ticket-pdf-title {
      font-size: 15px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
      flex: 1 !important;
      min-width: 0 !important;
      margin: 0 !important;
    }

    .zento-ticket-pdf-close {
      width: 32px !important;
      height: 32px !important;
      border: none !important;
      background: transparent !important;
      cursor: pointer !important;
      color: #64748b !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      border-radius: 8px !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .zento-ticket-pdf-close:hover {
      background: #f1f5f9 !important;
    }

    .zento-ticket-pdf-body {
      flex: 1 !important;
      min-height: 0 !important;
      position: relative !important;
      background: #f8fafc !important;
    }

    .zento-ticket-pdf-loading {
      position: absolute !important;
      inset: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 14px !important;
      font-weight: 500 !important;
      color: #64748b !important;
      background: #f8fafc !important;
    }

    .zento-ticket-pdf-loading[hidden] {
      display: none !important;
    }

    .zento-ticket-pdf-frame {
      width: 100% !important;
      height: 100% !important;
      border: none !important;
      background: white !important;
    }

    .zento-ticket-pdf-frame[hidden] {
      display: none !important;
    }
`;

export const createStyles = (primaryColor: string = '#6366f1') => {
  // Generate color variations based on primary color
  const lighterColor = `${primaryColor}20`; // 20% opacity
  const darkerColor = shadeColor(primaryColor, -20);

  return `
    /* CSS Reset for Widget Container - High specificity to override host page styles */
    .zento-widget-container,
    .zento-widget-container *,
    .zento-widget-container *::before,
    .zento-widget-container *::after {
      box-sizing: border-box !important;
      margin: 0 !important;
      padding: 0 !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      line-height: 1.5 !important;
      border: none !important;
      outline: none !important;
      text-decoration: none !important;
    }
    
    /* Restore specific padding/margin where needed */
    .zento-header { padding: 12px 16px !important; }
    .zento-messages { padding: 16px !important; gap: 10px !important; }
    .zento-message-bubble { padding: 10px 14px !important; }
    .zento-input-container { padding: 10px 16px !important; }
    .zento-input-wrapper { padding: 5px 5px 5px 14px !important; gap: 8px !important; }
    .zento-welcome { padding: 20px !important; }
    
    /* Animation Keyframes */
    @keyframes zento-fade-in {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(10px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }
    
    @keyframes zento-fade-out {
      from {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
      to {
        opacity: 0;
        transform: scale(0.95) translateY(10px);
      }
    }
    
    @keyframes zento-pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }
    
    @keyframes zento-bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-4px);
      }
    }
    
    @keyframes zento-typing-dot {
      0%, 60%, 100% {
        opacity: 0.3;
        transform: translateY(0);
      }
      30% {
        opacity: 1;
        transform: translateY(-4px);
      }
    }
    
    @keyframes zento-slide-up {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes zento-skeleton-shimmer {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
    
    /* Widget Container */
    .zento-widget-container {
      position: fixed !important;
      z-index: 2147483647 !important;
      font-size: 14px !important;
      color: #1e293b !important;
    }
    
    .zento-widget-container.zento-position-bottom-right {
      bottom: 20px !important;
      right: 20px !important;
    }
    
    .zento-widget-container.zento-position-bottom-left {
      bottom: 20px !important;
      left: 20px !important;
    }
    
    /* Floating Button */
    .zento-floating-button {
      width: 60px !important;
      height: 60px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1) !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      position: relative !important;
      overflow: visible !important;
      color: white !important;
    }
    
    .zento-floating-button::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%);
      border-radius: 50%;
      overflow: hidden;
      pointer-events: none;
    }
    
    .zento-floating-button:hover {
      transform: scale(1.08);
      box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2), 0 3px 10px rgba(0, 0, 0, 0.15);
    }
    
    .zento-floating-button:active {
      transform: scale(0.95);
    }
    
    .zento-floating-button svg {
      width: 28px !important;
      height: 28px !important;
      stroke: white !important;
      fill: none !important;
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      display: block !important;
      color: white !important;
    }
    
    .zento-floating-button svg[fill="currentColor"] {
      fill: white !important;
    }
    
    .zento-floating-button svg path {
      stroke: white !important;
    }
    
    .zento-chat-icon,
    .zento-close-icon {
      position: absolute !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      inset: 0 !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }
    
    .zento-close-icon {
      opacity: 0 !important;
      transform: rotate(-90deg) scale(0) !important;
    }
    
    .zento-floating-button.zento-open .zento-chat-icon {
      opacity: 0 !important;
      transform: rotate(90deg) scale(0) !important;
    }
    
    .zento-floating-button.zento-open .zento-close-icon {
      opacity: 1 !important;
      transform: rotate(0) scale(1) !important;
    }
    
    /* Custom Bubble Icon */
    .zento-custom-bubble-icon {
      width: 32px !important;
      height: 32px !important;
      border-radius: 50% !important;
      object-fit: cover !important;
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      display: block !important;
    }
    
    .zento-floating-button.zento-open .zento-custom-bubble-icon {
      opacity: 0 !important;
      transform: rotate(90deg) scale(0) !important;
    }
    
    /* Unread Badge on Floating Button */
    /* display must NOT use !important so JS can hide the badge with inline style */
    .zento-unread-badge {
      position: absolute !important;
      top: -4px !important;
      right: -4px !important;
      min-width: 22px !important;
      height: 22px !important;
      padding: 0 6px !important;
      border-radius: 11px !important;
      background: #ef4444 !important;
      color: white !important;
      font-size: 12px !important;
      font-weight: 700 !important;
      display: flex;
      align-items: center !important;
      justify-content: center !important;
      animation: zento-pulse 2s infinite !important;
      border: 2px solid white !important;
      box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4) !important;
      z-index: 1 !important;
    }
    
    /* Toast Notification */
    .zento-toast {
      position: absolute !important;
      bottom: 80px !important;
      width: 340px !important;
      max-width: calc(100vw - 40px) !important;
      background: white !important;
      border-radius: 14px !important;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
      padding: 10px !important;
      cursor: pointer !important;
      animation: zento-slide-up 0.3s ease-out !important;
      z-index: 2147483646 !important;
      display: flex !important;
      align-items: flex-start !important;
      gap: 12px !important;
      transition: transform 0.2s ease, box-shadow 0.2s ease !important;
    }

    .zento-toast:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
    
    .zento-position-bottom-right .zento-toast {
      right: 0 !important;
    }
    
    .zento-position-bottom-left .zento-toast {
      left: 0 !important;
    }
    
    .zento-toast-hiding {
      opacity: 0 !important;
      transform: translateY(10px) !important;
      transition: all 0.2s ease-out !important;
    }

    .zento-toast-avatar {
      width: 36px !important;
      height: 36px !important;
      min-width: 36px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
    }

    .zento-toast-avatar-letter {
      color: white !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      text-transform: uppercase !important;
      line-height: 1 !important;
    }

    .zento-toast-avatar-img {
      width: 100% !important;
      height: 100% !important;
      border-radius: 50% !important;
      object-fit: cover !important;
    }

    .zento-toast-body {
      flex: 1 !important;
      min-width: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 2px !important;
    }
    
    .zento-toast-header {
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
      gap: 8px !important;
    }
    
    .zento-toast-sender {
      font-weight: 600 !important;
      font-size: 13px !important;
      color: #1e293b !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
      flex: 1 !important;
    }

    .zento-toast-time {
      font-size: 11px !important;
      color: #94a3b8 !important;
      flex-shrink: 0 !important;
    }
    
    .zento-toast-content {
      font-size: 12px !important;
      color: #64748b !important;
      line-height: 1.4 !important;
      word-break: break-word !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      display: -webkit-box !important;
      -webkit-line-clamp: 2 !important;
      -webkit-box-orient: vertical !important;
    }

    .zento-toast-arrow {
      flex-shrink: 0 !important;
      color: #cbd5e1 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      align-self: center !important;
      transition: color 0.15s ease, transform 0.15s ease !important;
    }

    .zento-toast-arrow svg {
      width: 20px !important;
      height: 20px !important;
      stroke: currentColor !important;
      fill: none !important;
    }

    .zento-toast:hover .zento-toast-arrow {
      color: ${primaryColor} !important;
      transform: translateX(2px) !important;
    }
    
    .zento-toast-close {
      position: absolute !important;
      top: 8px !important;
      right: 8px !important;
      background: none !important;
      border: none !important;
      padding: 4px !important;
      cursor: pointer !important;
      color: #94a3b8 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      border-radius: 6px !important;
      transition: all 0.15s !important;
      opacity: 0 !important;
    }

    .zento-toast:hover .zento-toast-close {
      opacity: 1 !important;
    }
    
    .zento-toast-close:hover {
      background: #f1f5f9 !important;
      color: #64748b !important;
    }
    
    .zento-toast-close svg {
      width: 14px !important;
      height: 14px !important;
    }
    
    /* Chat Window */
    .zento-chat-window {
      position: absolute !important;
      bottom: 80px !important;
      width: 420px !important;
      max-width: calc(100vw - 40px) !important;
      height: 650px !important;
      max-height: calc(100vh - 120px) !important;
      background: white !important;
      border-radius: 16px !important;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      display: flex !important;
      flex-direction: column !important;
      overflow: hidden !important;
      opacity: 0;
      pointer-events: none;
      transform: scale(0.95) translateY(10px);
    }
    
    .zento-widget-container.zento-position-bottom-right .zento-chat-window {
      right: 0;
    }
    
    .zento-widget-container.zento-position-bottom-left .zento-chat-window {
      left: 0;
    }
    
    .zento-chat-window.zento-visible {
      opacity: 1;
      pointer-events: auto;
      transform: scale(1) translateY(0);
      animation: zento-fade-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .zento-chat-window.zento-closing {
      animation: zento-fade-out 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    /* Header */
    .zento-header {
      background: white !important;
      padding: 10px 16px !important;
      display: flex !important;
      align-items: center !important;
      gap: 10px !important;
      position: relative !important;
      flex-shrink: 0 !important;
      border-bottom: 1px solid #f1f5f9 !important;
    }
    
    .zento-header-avatar {
      width: 36px !important;
      height: 36px !important;
      min-width: 36px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
    }
    
    .zento-header-avatar-letter {
      color: white !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      text-transform: uppercase !important;
      line-height: 1 !important;
    }
    
    .zento-header-avatar svg {
      width: 18px !important;
      height: 18px !important;
      fill: white !important;
    }
    
    /* Header Logo */
    .zento-header-logo {
      width: 36px !important;
      height: 36px !important;
      border-radius: 50% !important;
      object-fit: cover !important;
      flex-shrink: 0 !important;
      background: ${lighterColor} !important;
    }
    
    .zento-header-info {
      flex: 1 !important;
      min-width: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 1px !important;
    }
    
    .zento-header-title {
      color: #1e293b !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      margin: 0 !important;
      letter-spacing: -0.01em !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }
    
    .zento-header-status {
      color: #64748b !important;
      font-size: 11.5px !important;
      display: flex !important;
      align-items: center !important;
      gap: 5px !important;
    }
    
    .zento-status-dot {
      width: 7px !important;
      height: 7px !important;
      border-radius: 50% !important;
      background: #22c55e !important;
      flex-shrink: 0 !important;
    }
    
    .zento-close-button {
      width: 30px !important;
      height: 30px !important;
      border-radius: 7px !important;
      background: transparent !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.2s ease !important;
      color: #64748b !important;
      flex-shrink: 0 !important;
    }
    
    .zento-close-button:hover {
      background: #f1f5f9 !important;
      color: #1e293b !important;
    }
    
    .zento-close-button svg {
      width: 18px !important;
      height: 18px !important;
      stroke: currentColor !important;
      fill: none !important;
    }
    
    /* Menu Button (back to list) */
    .zento-menu-button {
      width: 30px !important;
      height: 30px !important;
      border-radius: 7px !important;
      background: transparent !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.2s ease !important;
      flex-shrink: 0 !important;
      color: #64748b !important;
    }
    
    .zento-menu-button:hover {
      background: #f1f5f9 !important;
      color: ${primaryColor} !important;
    }
    
    .zento-menu-button svg {
      width: 18px !important;
      height: 18px !important;
      stroke: currentColor !important;
      fill: none !important;
    }
    
    /* Conversation List Panel */
    .zento-conversation-list {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: white;
      z-index: 10;
      display: flex;
      flex-direction: column;
      animation: zento-slide-up 0.2s ease-out;
    }
    
    .zento-conversation-list-header {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      padding: 12px 16px !important;
      background: white !important;
      flex-shrink: 0 !important;
      border-bottom: 1px solid #f1f5f9 !important;
    }

    .zento-conversation-new-primary {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
      width: 100% !important;
      padding: 12px 16px !important;
      margin: 0 !important;
      border: none !important;
      border-bottom: 1px solid ${primaryColor}33 !important;
      background: ${primaryColor} !important;
      color: #ffffff !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      cursor: pointer !important;
      flex-shrink: 0 !important;
      letter-spacing: -0.01em !important;
      transition: filter 0.15s ease !important;
    }

    .zento-conversation-new-primary:hover {
      filter: brightness(0.94) !important;
    }

    .zento-conversation-new-primary:active {
      filter: brightness(0.88) !important;
    }

    .zento-conversation-new-primary:focus-visible {
      outline: 2px solid #ffffff !important;
      outline-offset: -4px !important;
    }

    .zento-conversation-new-primary svg {
      width: 20px !important;
      height: 20px !important;
      stroke: currentColor !important;
      fill: none !important;
      flex-shrink: 0 !important;
    }

    .zento-conversation-new-primary-label {
      line-height: 1.2 !important;
    }
    
    .zento-back-button {
      width: 32px !important;
      height: 32px !important;
      border-radius: 8px !important;
      background: transparent !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.2s ease !important;
      flex-shrink: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    
    .zento-new-chat-button {
      width: 32px !important;
      height: 32px !important;
      border-radius: 8px !important;
      background: transparent !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.2s ease !important;
      flex-shrink: 0 !important;
      color: #64748b !important;
    }
    
    .zento-new-chat-button:hover {
      background: #f1f5f9 !important;
      color: ${primaryColor} !important;
    }
    
    .zento-new-chat-button svg {
      width: 20px !important;
      height: 20px !important;
      stroke: currentColor !important;
      fill: none !important;
    }
    
    .zento-conversation-list-title {
      flex: 1 !important;
      color: #1e293b !important;
      font-size: 15px !important;
      font-weight: 600 !important;
      text-align: center !important;
      letter-spacing: -0.02em !important;
    }
    
    .zento-conversation-close-btn {
      width: 32px !important;
      height: 32px !important;
      border-radius: 8px !important;
      background: transparent !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.2s ease !important;
      flex-shrink: 0 !important;
      color: #64748b !important;
    }
    
    .zento-conversation-close-btn:hover {
      background: #f1f5f9 !important;
      color: #1e293b !important;
    }
    
    .zento-conversation-close-btn svg {
      width: 20px !important;
      height: 20px !important;
      stroke: currentColor !important;
      fill: none !important;
    }
    
    .zento-conversation-items {
      flex: 1 !important;
      overflow-y: auto !important;
      padding: 0 !important;
      background: white !important;
      display: flex !important;
      flex-direction: column !important;
    }
    
    .zento-conversation-items::-webkit-scrollbar {
      width: 5px !important;
    }
    
    .zento-conversation-items::-webkit-scrollbar-track {
      background: transparent !important;
    }
    
    .zento-conversation-items::-webkit-scrollbar-thumb {
      background: #e2e8f0 !important;
      border-radius: 10px !important;
    }
    
    .zento-conversation-item {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      gap: 10px !important;
      padding: 10px 16px !important;
      background: white !important;
      border: none !important;
      border-bottom: 1px solid #f1f5f9 !important;
      border-radius: 0 !important;
      cursor: pointer !important;
      text-align: left !important;
      transition: background 0.15s ease !important;
      width: 100% !important;
    }
    
    .zento-conversation-item:hover {
      background: #f8fafc !important;
    }
    
    .zento-conversation-item:last-child {
      border-bottom: none !important;
    }
    
    .zento-conversation-item.zento-active {
      background: ${lighterColor} !important;
    }
    
    .zento-conversation-item.zento-has-unread {
      background: white !important;
    }
    
    .zento-conversation-item.zento-has-unread:hover {
      background: #f8fafc !important;
    }
    
    /* Avatar */
    .zento-conversation-avatar {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
    }
    
    .zento-conversation-avatar-letter {
      color: white !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      text-transform: uppercase !important;
      line-height: 1 !important;
    }
    
    /* Content */
    .zento-conversation-content {
      flex: 1 !important;
      min-width: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 2px !important;
    }
    
    .zento-conversation-header {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 8px !important;
    }
    
    .zento-conversation-name {
      font-size: 13px !important;
      color: #1e293b !important;
      font-weight: 600 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
      line-height: 1.3 !important;
      flex: 1 !important;
      min-width: 0 !important;
    }
    
    .zento-conversation-time {
      font-size: 12px !important;
      color: #94a3b8 !important;
      flex-shrink: 0 !important;
    }
    
    .zento-conversation-item.zento-has-unread .zento-conversation-time {
      color: ${primaryColor} !important;
      font-weight: 500 !important;
    }
    
    .zento-conversation-preview {
      font-size: 12px !important;
      color: #64748b !important;
      font-weight: 400 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
      line-height: 1.4 !important;
    }
    
    .zento-conversation-item.zento-has-unread .zento-conversation-preview {
      color: #334155 !important;
      font-weight: 500 !important;
    }
    
    .zento-conversation-item.zento-has-unread .zento-conversation-name {
      color: #0f172a !important;
    }
    
    /* Arrow/Chevron */
    .zento-conversation-arrow {
      flex-shrink: 0 !important;
      color: #cbd5e1 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: color 0.15s ease, transform 0.15s ease !important;
    }
    
    .zento-conversation-arrow svg {
      width: 18px !important;
      height: 18px !important;
      stroke: currentColor !important;
      fill: none !important;
    }
    
    .zento-conversation-item:hover .zento-conversation-arrow {
      color: ${primaryColor} !important;
      transform: translateX(2px) !important;
    }
    
    /* Unread indicator dot */
    .zento-conversation-unread-dot {
      width: 10px !important;
      height: 10px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      flex-shrink: 0 !important;
      margin-left: 4px !important;
    }
    
    .zento-conversation-meta {
      display: none !important;
    }
    
    .zento-conversation-unread-badge {
      display: none !important;
    }
    
    .zento-conversation-count {
      display: none !important;
    }
    
    .zento-conversation-error {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      color: #ef4444;
      font-size: 14px;
    }

    /* Skeleton loaders */
    .zento-skeleton {
      background: linear-gradient(
        90deg,
        #e2e8f0 0%,
        #f1f5f9 35%,
        #e8edf3 50%,
        #f1f5f9 65%,
        #e2e8f0 100%
      ) !important;
      background-size: 200% 100% !important;
      animation: zento-skeleton-shimmer 1.35s ease-in-out infinite !important;
      border: none !important;
    }

    .zento-conversation-skeleton {
      display: flex !important;
      flex-direction: column !important;
      flex: 1 !important;
      width: 100% !important;
      padding: 0 !important;
      min-height: 0 !important;
    }

    .zento-conversation-skeleton-compact .zento-skeleton-conversation-row {
      padding: 8px 16px !important;
    }

    .zento-conversation-skeleton-compact .zento-skeleton-avatar {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
    }

    .zento-skeleton-conversation-row {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      gap: 10px !important;
      padding: 10px 16px !important;
      border-bottom: 1px solid #f1f5f9 !important;
      width: 100% !important;
      box-sizing: border-box !important;
    }

    .zento-skeleton-avatar {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
      border-radius: 50% !important;
      flex-shrink: 0 !important;
    }

    .zento-skeleton-conversation-row-text {
      flex: 1 !important;
      min-width: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 8px !important;
    }

    .zento-skeleton-line {
      height: 10px !important;
      border-radius: 5px !important;
      max-width: 100% !important;
    }

    .zento-skeleton-line-title {
      width: 42% !important;
      height: 12px !important;
    }

    .zento-skeleton-line-preview {
      width: 78% !important;
    }

    .zento-conversation-load-more-skeleton {
      width: 100% !important;
      padding: 0 !important;
    }

    .zento-messages-skeleton {
      display: flex !important;
      flex-direction: column !important;
      gap: 14px !important;
      padding: 16px !important;
      width: 100% !important;
      box-sizing: border-box !important;
    }

    .zento-skeleton-bubble {
      display: flex !important;
      flex-direction: column !important;
      gap: 8px !important;
      max-width: 100% !important;
    }

    .zento-skeleton-bubble-incoming {
      align-self: flex-start !important;
      width: 78% !important;
    }

    .zento-skeleton-bubble-outgoing {
      align-self: flex-end !important;
      width: 62% !important;
    }

    .zento-skeleton-bubble-line {
      height: 11px !important;
      border-radius: 8px !important;
      width: 100% !important;
    }

    .zento-skeleton-bubble-line-short {
      width: 72% !important;
    }

    .zento-skeleton-bubble-line-medium {
      width: 88% !important;
    }

    .zento-messages-load-more-skeleton {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 6px !important;
      padding: 8px 12px 10px !important;
      padding-top: 4px !important;
    }

    .zento-skeleton-msg-load-pill {
      height: 8px !important;
      border-radius: 4px !important;
      width: 100px !important;
    }

    .zento-skeleton-msg-load-pill-short {
      width: 56px !important;
    }
    
    .zento-conversation-empty {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 40px 24px !important;
      text-align: center !important;
      flex: 1 !important;
    }
    
    .zento-empty-icon {
      width: 64px !important;
      height: 64px !important;
      margin-bottom: 20px !important;
      color: #c7cdd5 !important;
      background: #f1f5f9 !important;
      padding: 16px !important;
      border-radius: 50% !important;
    }
    
    .zento-empty-icon svg {
      width: 100% !important;
      height: 100% !important;
      stroke: currentColor !important;
    }
    
    .zento-conversation-empty p {
      color: #64748b !important;
      font-size: 13px !important;
      margin: 0 0 20px 0 !important;
      line-height: 1.5 !important;
    }
    
    .zento-start-chat-btn {
      padding: 10px 22px !important;
      background: linear-gradient(135deg, ${primaryColor} 0%, ${darkerColor} 100%) !important;
      color: white !important;
      border: none !important;
      border-radius: 22px !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      cursor: pointer !important;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
      box-shadow: 0 4px 15px ${primaryColor}40 !important;
      letter-spacing: 0.01em !important;
    }
    
    .zento-start-chat-btn:hover {
      background: linear-gradient(135deg, ${darkerColor} 0%, ${primaryColor} 100%) !important;
      transform: translateY(-2px) scale(1.02) !important;
      box-shadow: 0 6px 20px ${primaryColor}50 !important;
    }
    
    .zento-start-chat-btn:active {
      transform: translateY(0) scale(0.98) !important;
      box-shadow: 0 2px 10px ${primaryColor}30 !important;
    }
    
    .zento-messages-load-more {
      flex-shrink: 0 !important;
      width: 100% !important;
      text-align: center !important;
      padding: 6px 0 2px !important;
    }
    .zento-messages-load-more-btn {
      background: transparent !important;
      border: none !important;
      color: #64748b !important;
      font-size: 12px !important;
      cursor: pointer !important;
      text-decoration: underline !important;
      padding: 4px 8px !important;
    }
    .zento-messages-load-more-btn:hover {
      color: #334155 !important;
    }
    .zento-conversation-load-more-sentinel {
      width: 100% !important;
    }
    .zento-conversation-load-more-spacer {
      height: 4px !important;
    }

    .zento-inbox-tabs {
      display: flex !important;
      flex-shrink: 0 !important;
      border-top: 1px solid #e2e8f0 !important;
      background: #f8fafc !important;
    }

    .zento-inbox-tabs.zento-inbox-panel-hidden,
    .zento-inbox-tabs[hidden] {
      display: none !important;
    }

    .zento-inbox-tab {
      flex: 1 !important;
      padding: 12px 8px !important;
      border: none !important;
      background: transparent !important;
      color: #64748b !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      cursor: pointer !important;
      font-family: inherit !important;
      transition: color 0.15s ease, background 0.15s ease !important;
    }

    .zento-inbox-tab:hover {
      color: #334155 !important;
      background: #f1f5f9 !important;
    }

    .zento-inbox-tab-active {
      color: ${primaryColor} !important;
      box-shadow: inset 0 -2px 0 ${primaryColor} !important;
      background: white !important;
    }

    .zento-ticket-items {
      flex: 1 !important;
      overflow-y: auto !important;
      display: flex !important;
      flex-direction: column !important;
      background: white !important;
    }

    .zento-conversation-items.zento-inbox-panel-hidden,
    .zento-ticket-dev-usage.zento-inbox-panel-hidden,
    .zento-ticket-items.zento-inbox-panel-hidden {
      display: none !important;
    }

    .zento-ticket-dev-usage {
      flex-shrink: 0 !important;
      padding: 8px 12px !important;
      border-bottom: 1px solid #e2e8f0 !important;
      background: #f8fafc !important;
      position: relative !important;
    }

    .zento-ticket-dev-usage-inner {
      position: relative !important;
    }

    .zento-ticket-dev-usage-loading .zento-ticket-dev-usage-skeleton {
      display: block !important;
      height: 36px !important;
      border-radius: 8px !important;
      background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%) !important;
      background-size: 200% 100% !important;
      animation: zento-skeleton-shimmer 1.2s ease-in-out infinite !important;
    }

    .zento-ticket-dev-usage-trigger {
      display: flex !important;
      align-items: center !important;
      gap: 8px !important;
      width: 100% !important;
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
      cursor: pointer !important;
      font-family: inherit !important;
      text-align: left !important;
    }

    .zento-ticket-dev-usage-trigger-main {
      display: flex !important;
      align-items: baseline !important;
      flex-wrap: wrap !important;
      gap: 4px 6px !important;
      min-width: 0 !important;
      flex: 1 !important;
    }

    .zento-ticket-dev-usage-label {
      font-size: 12px !important;
      font-weight: 600 !important;
      color: #64748b !important;
    }

    .zento-ticket-dev-usage-month {
      font-size: 12px !important;
      font-weight: 600 !important;
      color: #334155 !important;
    }

    .zento-ticket-dev-usage-month::before {
      content: "·" !important;
      margin-right: 6px !important;
      color: #cbd5e1 !important;
    }

    .zento-ticket-dev-usage-total {
      font-size: 13px !important;
      font-weight: 700 !important;
      color: ${primaryColor} !important;
      white-space: nowrap !important;
      flex-shrink: 0 !important;
    }

    .zento-ticket-dev-usage-chevron {
      display: inline-flex !important;
      color: #94a3b8 !important;
      transition: transform 0.15s ease !important;
      flex-shrink: 0 !important;
    }

    .zento-ticket-dev-usage-chevron svg {
      width: 16px !important;
      height: 16px !important;
    }

    .zento-ticket-dev-usage-chevron-open {
      transform: rotate(180deg) !important;
    }

    .zento-ticket-dev-usage-picker {
      position: absolute !important;
      left: 0 !important;
      right: 0 !important;
      top: calc(100% + 4px) !important;
      z-index: 20 !important;
      display: flex !important;
      flex-direction: column !important;
      max-height: 180px !important;
      overflow-y: auto !important;
      background: white !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 8px !important;
      box-shadow: 0 8px 20px rgba(15, 23, 42, 0.1) !important;
    }

    .zento-ticket-dev-usage-picker[hidden] {
      display: none !important;
    }

    .zento-ticket-dev-usage-month-option {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 8px !important;
      width: 100% !important;
      padding: 8px 10px !important;
      border: none !important;
      border-bottom: 1px solid #f1f5f9 !important;
      background: white !important;
      cursor: pointer !important;
      font-family: inherit !important;
      text-align: left !important;
    }

    .zento-ticket-dev-usage-month-option:last-child {
      border-bottom: none !important;
    }

    .zento-ticket-dev-usage-month-option:hover {
      background: #f8fafc !important;
    }

    .zento-ticket-dev-usage-month-option-active {
      background: ${primaryColor}10 !important;
    }

    .zento-ticket-dev-usage-month-option-label {
      font-size: 12px !important;
      font-weight: 600 !important;
      color: #334155 !important;
    }

    .zento-ticket-dev-usage-month-option-value {
      font-size: 11px !important;
      font-weight: 600 !important;
      color: #64748b !important;
      white-space: nowrap !important;
    }

    .zento-ticket-item {
      display: flex !important;
      align-items: flex-start !important;
      gap: 12px !important;
      padding: 14px 16px !important;
      border-bottom: 1px solid #f1f5f9 !important;
      background: white !important;
    }

    .zento-ticket-item-main {
      flex: 1 !important;
      min-width: 0 !important;
    }

    .zento-ticket-item-header {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 8px !important;
      margin-bottom: 4px !important;
    }

    .zento-ticket-label {
      font-size: 12px !important;
      font-weight: 700 !important;
      color: ${primaryColor} !important;
      letter-spacing: 0.02em !important;
    }

    .zento-ticket-status {
      font-size: 11px !important;
      font-weight: 600 !important;
      padding: 2px 8px !important;
      border-radius: 999px !important;
      background: #f1f5f9 !important;
      color: #475569 !important;
      white-space: nowrap !important;
    }

    .zento-ticket-status-abierto {
      background: #dbeafe !important;
      color: #1d4ed8 !important;
    }

    .zento-ticket-status-en_progreso {
      background: #fef9c3 !important;
      color: #a16207 !important;
    }

    .zento-ticket-status-en_espera {
      background: #ffedd5 !important;
      color: #c2410c !important;
    }

    .zento-ticket-status-resuelto {
      background: #dcfce7 !important;
      color: #15803d !important;
    }

    .zento-ticket-status-cerrado {
      background: #f1f5f9 !important;
      color: #475569 !important;
    }

    .zento-ticket-title {
      font-size: 14px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
      line-height: 1.35 !important;
      margin-bottom: 4px !important;
    }

    .zento-ticket-meta {
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      gap: 6px !important;
      font-size: 12px !important;
      color: #64748b !important;
    }

    .zento-ticket-meta-sep {
      color: #cbd5e1 !important;
    }

    .zento-ticket-pdf-btn {
      flex-shrink: 0 !important;
      padding: 8px 12px !important;
      border-radius: 8px !important;
      border: 1px solid ${primaryColor}33 !important;
      background: white !important;
      color: ${primaryColor} !important;
      font-size: 12px !important;
      font-weight: 700 !important;
      cursor: pointer !important;
      font-family: inherit !important;
    }

    .zento-ticket-pdf-btn:hover {
      background: ${primaryColor}12 !important;
    }

    .zento-ticket-skeleton {
      padding: 8px 16px !important;
    }

    .zento-ticket-skeleton-compact {
      padding: 4px 16px !important;
    }

    .zento-ticket-load-more-sentinel {
      width: 100% !important;
    }

    .zento-skeleton-ticket-row {
      padding: 12px 0 !important;
      border-bottom: 1px solid #f1f5f9 !important;
    }

    /* Scroll area wrapper + “Ir al final” pill (when not at bottom) */
    .zento-messages-wrap {
      flex: 1 !important;
      min-height: 0 !important;
      position: relative !important;
      display: flex !important;
      flex-direction: column !important;
      width: 100% !important;
    }

    .zento-jump-to-bottom {
      position: absolute !important;
      bottom: 12px !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      z-index: 6 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 6px !important;
      padding: 8px 16px !important;
      border: none !important;
      border-radius: 999px !important;
      background: ${primaryColor} !important;
      color: #fff !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      font-family: inherit !important;
      cursor: pointer !important;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
      letter-spacing: -0.02em !important;
    }

    .zento-jump-to-bottom:hover {
      filter: brightness(1.06) !important;
    }

    .zento-jump-to-bottom:focus-visible {
      outline: 2px solid ${primaryColor} !important;
      outline-offset: 2px !important;
    }

    .zento-jump-to-bottom[hidden] {
      display: none !important;
    }

    .zento-jump-to-bottom-icon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
    }

    .zento-jump-to-bottom-icon svg {
      display: block !important;
    }
    
    /* Messages Container */
    .zento-messages {
      flex: 1 !important;
      min-height: 0 !important;
      overflow-y: auto !important;
      padding: 14px !important;
      background: #f8fafc !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 8px !important;
      scroll-behavior: smooth !important;
    }
    
    .zento-messages::-webkit-scrollbar {
      width: 5px !important;
    }
    
    .zento-messages::-webkit-scrollbar-track {
      background: transparent !important;
    }
    
    .zento-messages::-webkit-scrollbar-thumb {
      background: #e2e8f0 !important;
      border-radius: 10px !important;
    }
    
    .zento-messages::-webkit-scrollbar-thumb:hover {
      background: #cbd5e1 !important;
    }
    
    /* Message Bubbles */
    .zento-message {
      display: flex !important;
      flex-direction: row !important;
      align-items: flex-end !important;
      gap: 8px !important;
      max-width: 85% !important;
      animation: zento-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
    }
    
    .zento-message.zento-user {
      align-self: flex-end !important;
      flex-direction: row-reverse !important;
    }
    
    .zento-message.zento-assistant {
      align-self: flex-start !important;
    }
    
    .zento-message-avatar {
      width: 26px !important;
      height: 26px !important;
      min-width: 26px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
      margin-bottom: 16px !important;
    }
    
    .zento-message-avatar-letter {
      color: white !important;
      font-size: 10px !important;
      font-weight: 600 !important;
      text-transform: uppercase !important;
      line-height: 1 !important;
    }
    
    .zento-message-avatar-img {
      width: 100% !important;
      height: 100% !important;
      border-radius: 50% !important;
      object-fit: cover !important;
    }
    
    .zento-message.zento-user .zento-message-avatar {
      display: none !important;
    }
    
    .zento-message-content {
      display: flex !important;
      flex-direction: column !important;
      gap: 2px !important;
      min-width: 0 !important;
    }
    
    .zento-message-bubble {
      padding: 8px 12px !important;
      border-radius: 16px !important;
      font-size: 13.5px !important;
      line-height: 1.5 !important;
      word-break: break-word !important;
    }
    
    .zento-message.zento-user .zento-message-bubble {
      background: ${primaryColor} !important;
      color: white !important;
      border-bottom-right-radius: 6px !important;
    }
    
    .zento-message.zento-assistant .zento-message-bubble {
      background: white !important;
      color: #1e293b !important;
      border-bottom-left-radius: 6px !important;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
    }
    
    .zento-message-time {
      font-size: 10px !important;
      color: #94a3b8 !important;
      padding: 0 2px !important;
    }
    
    .zento-message.zento-user .zento-message-time {
      text-align: right !important;
    }
    
    .zento-message.zento-assistant .zento-message-time {
      text-align: left !important;
    }
    
    /* Typing Indicator */
    .zento-typing-indicator {
      display: flex !important;
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 4px !important;
      padding: 8px 12px !important;
      background: #f8fafc !important;
      border-radius: 18px !important;
      border-bottom-left-radius: 4px !important;
      border: 1px solid #f1f5f9 !important;
      align-self: flex-start !important;
      animation: zento-slide-up 0.3s ease-out !important;
      margin-bottom: 10px !important;
      max-width: 85% !important;
    }
    
    .zento-typing-dots {
      display: flex !important;
      align-items: center !important;
      gap: 5px !important;
    }
    
    .zento-typing-dot {
      width: 6px !important;
      height: 6px !important;
      border-radius: 50% !important;
      background: #94a3b8 !important;
    }
    
    .zento-typing-dot:nth-child(1) {
      animation: zento-typing-dot 1.4s ease-in-out infinite;
    }
    
    .zento-typing-dot:nth-child(2) {
      animation: zento-typing-dot 1.4s ease-in-out infinite 0.2s;
    }
    
    .zento-typing-dot:nth-child(3) {
      animation: zento-typing-dot 1.4s ease-in-out infinite 0.4s;
    }
    
    .zento-typing-status {
      font-size: 11px !important;
      color: #64748b !important;
      font-style: italic !important;
      animation: zento-fade-in 0.3s ease-out !important;
    }
    
    @keyframes zento-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    /* Welcome Message */
    .zento-welcome {
      text-align: center !important;
      padding: 40px 24px !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      flex: 1 !important;
    }
    
    .zento-welcome-icon {
      width: 64px !important;
      height: 64px !important;
      margin: 0 auto 16px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      overflow: hidden !important;
    }
    
    .zento-welcome-icon svg {
      width: 32px !important;
      height: 32px !important;
      fill: white !important;
    }
    
    .zento-welcome-avatar-letter {
      color: white !important;
      font-size: 24px !important;
      font-weight: 600 !important;
      text-transform: uppercase !important;
      line-height: 1 !important;
    }
    
    /* Welcome Custom Logo */
    .zento-welcome-logo {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      display: block !important;
    }
    
    .zento-welcome-title {
      font-size: 19px !important;
      font-weight: 700 !important;
      color: #1e293b !important;
      margin-bottom: 6px !important;
      letter-spacing: -0.02em !important;
    }
    
    .zento-welcome-text {
      font-size: 13.5px !important;
      color: #64748b !important;
      margin: 0 !important;
      line-height: 1.5 !important;
      max-width: 300px !important;
    }
    
    /* Human request — header: icon + short label */
    .zento-human-request-button {
      height: 30px !important;
      min-height: 30px !important;
      width: auto !important;
      min-width: 30px !important;
      max-width: min(132px, 34vw) !important;
      border-radius: 7px !important;
      background: transparent !important;
      border: none !important;
      padding: 0 6px 0 4px !important;
      gap: 4px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      color: #64748b !important;
      cursor: pointer !important;
      transition: background 0.2s ease, color 0.2s ease !important;
      flex-shrink: 1 !important;
    }

    .zento-human-request-text {
      font-size: 11px !important;
      font-weight: 600 !important;
      letter-spacing: -0.02em !important;
      line-height: 1 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      max-width: 78px !important;
    }

    .zento-human-request-button:hover:not(:disabled) {
      background: #f1f5f9 !important;
      color: ${primaryColor} !important;
    }

    .zento-human-request-button:disabled {
      cursor: not-allowed !important;
      opacity: 0.55 !important;
      color: #94a3b8 !important;
    }

    .zento-human-request-button.zento-human-request-state-queue:disabled {
      opacity: 1 !important;
      color: ${primaryColor} !important;
    }

    .zento-human-request-button.zento-human-request-state-active:disabled {
      opacity: 1 !important;
      color: #16a34a !important;
    }

    .zento-human-request-button svg {
      width: 18px !important;
      height: 18px !important;
      stroke: currentColor !important;
      fill: none !important;
    }
    
    /* Human Agent Message Styles */
    .zento-message.zento-human-agent {
      align-self: flex-start !important;
    }

    .zento-message.zento-human-agent .zento-message-avatar {
      background: #16a34a !important;
    }

    .zento-message.zento-human-agent .zento-message-bubble {
      background: #16a34a !important;
      color: white !important;
      border-bottom-left-radius: 6px !important;
    }

    .zento-human-agent-label {
      display: flex !important;
      align-items: center !important;
      gap: 4px !important;
      font-size: 11px !important;
      color: #16a34a !important;
      margin-bottom: 2px !important;
      font-weight: 500 !important;
      padding-left: 4px !important;
    }

    .zento-human-agent-label svg {
      width: 12px !important;
      height: 12px !important;
      stroke: currentColor !important;
    }

    /* Ticket reply HTML (TipTap) inside message bubbles */
    .zento-message-bubble .zento-html-content {
      font-size: inherit !important;
      line-height: inherit !important;
    }

    .zento-message-bubble .zento-html-content p {
      margin: 0 0 0.5em 0 !important;
    }

    .zento-message-bubble .zento-html-content p:last-child {
      margin-bottom: 0 !important;
    }

    .zento-message-bubble .zento-html-content ul,
    .zento-message-bubble .zento-html-content ol {
      margin: 0.35em 0 0.5em 0 !important;
      padding-left: 1.25em !important;
    }

    .zento-message-bubble .zento-html-content li {
      margin: 0.15em 0 !important;
    }

    .zento-message-bubble .zento-html-content h1,
    .zento-message-bubble .zento-html-content h2,
    .zento-message-bubble .zento-html-content h3 {
      font-weight: 600 !important;
      margin: 0.5em 0 0.35em 0 !important;
      line-height: 1.3 !important;
    }

    .zento-message-bubble .zento-html-content h1 { font-size: 1.15em !important; }
    .zento-message-bubble .zento-html-content h2 { font-size: 1.08em !important; }
    .zento-message-bubble .zento-html-content h3 { font-size: 1em !important; }

    .zento-message-bubble .zento-html-content blockquote {
      margin: 0.35em 0 !important;
      padding-left: 0.75em !important;
      border-left: 3px solid rgba(148, 163, 184, 0.6) !important;
      opacity: 0.95 !important;
    }

    .zento-message-bubble .zento-html-content pre,
    .zento-message-bubble .zento-html-content code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important;
      font-size: 0.92em !important;
    }

    .zento-message-bubble .zento-html-content pre {
      margin: 0.35em 0 !important;
      padding: 0.5em 0.65em !important;
      border-radius: 6px !important;
      overflow-x: auto !important;
      background: rgba(15, 23, 42, 0.06) !important;
    }

    .zento-message-bubble .zento-html-content img {
      max-width: 100% !important;
      height: auto !important;
      border-radius: 8px !important;
      margin: 0.25em 0 !important;
    }

    .zento-message-bubble .zento-html-content table {
      width: 100% !important;
      border-collapse: collapse !important;
      margin: 0.35em 0 !important;
      font-size: 0.92em !important;
    }

    .zento-message-bubble .zento-html-content th,
    .zento-message-bubble .zento-html-content td {
      border: 1px solid rgba(148, 163, 184, 0.45) !important;
      padding: 0.25em 0.45em !important;
      text-align: left !important;
    }

    .zento-message-bubble .zento-html-content a {
      color: ${primaryColor} !important;
      text-decoration: underline !important;
    }

    .zento-message.zento-user .zento-message-bubble .zento-html-content a,
    .zento-message.zento-human-agent .zento-message-bubble .zento-html-content a {
      color: white !important;
    }

    .zento-message-bubble .zento-html-content a.zento-message-ticket-link {
      display: inline-flex !important;
      align-items: center !important;
      gap: 4px !important;
      text-decoration: none !important;
      padding: 4px 8px !important;
      border-radius: 8px !important;
      background: rgba(255, 255, 255, 0.15) !important;
      margin-top: 0.35em !important;
    }

    .zento-message.zento-assistant .zento-message-bubble .zento-html-content a.zento-message-ticket-link {
      background: rgba(15, 23, 42, 0.06) !important;
      color: ${primaryColor} !important;
    }
    
    /* Human Request Modal */
    .zento-human-modal {
      position: fixed !important;
      inset: 0 !important;
      z-index: 2147483649 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      animation: zento-fade-in 0.2s ease-out !important;
    }
    
    .zento-human-modal.zento-closing {
      animation: zento-fade-out 0.2s ease-out forwards !important;
    }
    
    .zento-human-modal-backdrop {
      position: absolute !important;
      inset: 0 !important;
      background: rgba(0, 0, 0, 0.5) !important;
      backdrop-filter: blur(2px) !important;
    }
    
    .zento-human-modal-content {
      position: relative !important;
      background: white !important;
      border-radius: 16px !important;
      padding: 24px !important;
      width: 320px !important;
      max-width: calc(100% - 40px) !important;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
      animation: zento-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
    }
    
    .zento-human-modal-header {
      text-align: center !important;
      margin-bottom: 20px !important;
    }
    
    .zento-human-modal-icon {
      width: 56px !important;
      height: 56px !important;
      border-radius: 14px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin: 0 auto 14px !important;
    }
    
    .zento-human-modal-icon svg {
      width: 28px !important;
      height: 28px !important;
      stroke: ${primaryColor} !important;
    }
    
    .zento-human-modal-title {
      font-size: 18px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
      margin: 0 0 8px 0 !important;
    }
    
    .zento-human-modal-subtitle {
      font-size: 13px !important;
      color: #64748b !important;
      margin: 0 !important;
      line-height: 1.5 !important;
    }
    
    .zento-human-modal-form {
      display: flex !important;
      flex-direction: column !important;
      gap: 14px !important;
    }
    
    .zento-human-modal-field {
      display: flex !important;
      flex-direction: column !important;
      gap: 6px !important;
    }
    
    .zento-human-modal-field label {
      display: flex !important;
      align-items: center !important;
      gap: 6px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      color: #475569 !important;
    }
    
    .zento-human-modal-field label svg {
      width: 14px !important;
      height: 14px !important;
      stroke: #94a3b8 !important;
    }
    
    .zento-human-modal-field input {
      padding: 10px 14px !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 10px !important;
      font-size: 14px !important;
      color: #1e293b !important;
      background: #f8fafc !important;
      transition: all 0.2s ease !important;
    }
    
    .zento-human-modal-field input::placeholder {
      color: #94a3b8 !important;
    }
    
    .zento-human-modal-field input:focus {
      outline: none !important;
      border-color: ${primaryColor} !important;
      background: white !important;
      box-shadow: 0 0 0 3px ${lighterColor} !important;
    }
    
    .zento-human-modal-actions {
      display: flex !important;
      gap: 10px !important;
      margin-top: 6px !important;
    }
    
    .zento-human-modal-cancel {
      flex: 1 !important;
      padding: 10px 16px !important;
      background: #f1f5f9 !important;
      border: none !important;
      border-radius: 10px !important;
      font-size: 14px !important;
      font-weight: 500 !important;
      color: #64748b !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
    }
    
    .zento-human-modal-cancel:hover {
      background: #e2e8f0 !important;
      color: #475569 !important;
    }
    
    .zento-human-modal-submit {
      flex: 1 !important;
      padding: 10px 16px !important;
      border: none !important;
      border-radius: 10px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      color: white !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    }
    
    .zento-human-modal-submit:hover {
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    }

    /* Input Container */
    .zento-input-container {
      padding: 8px 14px 10px !important;
      background: white !important;
      border-top: 1px solid #f1f5f9 !important;
    }
    
    .zento-input-wrapper {
      display: flex !important;
      align-items: center !important;
      gap: 6px !important;
      background: #f8fafc !important;
      border-radius: 22px !important;
      padding: 4px 4px 4px 14px !important;
      transition: all 0.2s ease !important;
      border: 1px solid #e2e8f0 !important;
    }
    
    .zento-input-wrapper:focus-within {
      background: white !important;
      border-color: ${primaryColor} !important;
      box-shadow: 0 0 0 2px ${lighterColor} !important;
    }
    
    .zento-input {
      flex: 1 !important;
      border: none !important;
      background: transparent !important;
      font-size: 13.5px !important;
      color: #1e293b !important;
      resize: none !important;
      max-height: 100px !important;
      min-height: 18px !important;
      line-height: 1.5 !important;
      padding: 6px 0 !important;
      margin: 0 !important;
      overflow-y: auto !important;
    }
    
    .zento-input::placeholder {
      color: #94a3b8 !important;
      opacity: 1 !important;
    }
    
    .zento-input:focus {
      outline: none !important;
    }
    
    .zento-send-button {
      width: 32px !important;
      height: 32px !important;
      border-radius: 50% !important;
      background: ${primaryColor} !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
      transition: all 0.2s ease !important;
      position: relative !important;
      overflow: hidden !important;
    }
    
    .zento-send-button::before {
      content: '' !important;
      position: absolute !important;
      inset: 0 !important;
      background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%) !important;
      border-radius: 50% !important;
      opacity: 0 !important;
      transition: opacity 0.2s ease !important;
    }
    
    .zento-send-button:hover:not(:disabled)::before {
      opacity: 1 !important;
    }
    
    .zento-send-button:hover:not(:disabled) {
      transform: scale(1.05) !important;
    }
    
    .zento-send-button:active:not(:disabled) {
      transform: scale(0.95) !important;
    }
    
    .zento-send-button:disabled {
      opacity: 0.4 !important;
      cursor: not-allowed !important;
      background: #cbd5e1 !important;
    }
    
    .zento-send-button svg {
      width: 16px !important;
      height: 16px !important;
      fill: white !important;
      position: relative !important;
      z-index: 1 !important;
    }
    
    /* Attach Button */
    .zento-attach-button {
      width: 32px !important;
      height: 32px !important;
      border-radius: 10px !important;
      background: transparent !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.2s ease !important;
      flex-shrink: 0 !important;
      color: #94a3b8 !important;
    }
    
    .zento-attach-button:hover {
      background: ${lighterColor} !important;
      color: ${primaryColor} !important;
    }
    
    .zento-attach-button:disabled {
      opacity: 0.4 !important;
      cursor: not-allowed !important;
    }
    
    .zento-attach-button svg {
      width: 16px !important;
      height: 16px !important;
    }
    
    /* Attachment Preview - Note: display is controlled via inline style, don't use !important */
    .zento-attachment-preview {
      flex-wrap: wrap !important;
      gap: 8px !important;
      padding: 12px !important;
      background: #f8fafc !important;
      border-radius: 12px !important;
      margin-bottom: 8px !important;
      border: 1px solid #e2e8f0 !important;
    }
    
    .zento-attachment-item {
      display: flex !important;
      align-items: center !important;
      gap: 8px !important;
      padding: 6px 10px !important;
      background: white !important;
      border-radius: 8px !important;
      border: 1px solid #e2e8f0 !important;
      max-width: 150px !important;
      position: relative !important;
    }
    
    .zento-attachment-thumb {
      width: 32px !important;
      height: 32px !important;
      object-fit: cover !important;
      border-radius: 6px !important;
    }
    
    .zento-attachment-icon {
      width: 32px !important;
      height: 32px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background: #fef2f2 !important;
      border-radius: 6px !important;
      color: #ef4444 !important;
    }
    
    .zento-attachment-icon svg {
      width: 16px !important;
      height: 16px !important;
    }
    
    .zento-attachment-name {
      font-size: 11px !important;
      color: #475569 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      max-width: 80px !important;
    }
    
    .zento-attachment-remove {
      position: absolute !important;
      top: -6px !important;
      right: -6px !important;
      width: 18px !important;
      height: 18px !important;
      border-radius: 50% !important;
      background: #ef4444 !important;
      border: none !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      color: white !important;
      opacity: 0 !important;
      transition: opacity 0.2s ease !important;
    }
    
    .zento-attachment-item:hover .zento-attachment-remove {
      opacity: 1 !important;
    }
    
    .zento-attachment-remove svg {
      width: 10px !important;
      height: 10px !important;
    }
    
    /* Message Attachments */
    .zento-message-attachments {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
      margin-bottom: 8px !important;
    }
    
    .zento-message-attachment-link {
      display: block !important;
      position: relative !important;
      cursor: pointer !important;
      transition: opacity 0.2s ease !important;
    }
    
    .zento-message-attachment-link:hover {
      opacity: 0.85 !important;
    }
    
    .zento-message-attachment-link::after {
      content: '' !important;
      position: absolute !important;
      inset: 0 !important;
      background: rgba(0, 0, 0, 0.3) !important;
      border-radius: 10px !important;
      opacity: 0 !important;
      transition: opacity 0.2s ease !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
    
    .zento-message-attachment-link:hover::after {
      opacity: 1 !important;
    }
    
    .zento-message-attachment-img {
      max-width: 180px !important;
      max-height: 120px !important;
      object-fit: contain !important;
      border-radius: 10px !important;
      background: rgba(255, 255, 255, 0.1) !important;
      display: block !important;
    }
    
    .zento-message-attachment-file {
      display: flex !important;
      align-items: center !important;
      gap: 6px !important;
      padding: 8px 12px !important;
      background: rgba(255, 255, 255, 0.15) !important;
      border-radius: 8px !important;
      font-size: 12px !important;
      text-decoration: none !important;
      color: inherit !important;
      cursor: pointer !important;
      transition: background 0.2s ease !important;
    }
    
    .zento-message-attachment-file:hover {
      background: rgba(255, 255, 255, 0.25) !important;
    }
    
    .zento-message-attachment-file.zento-attachment-pending {
      cursor: default !important;
      opacity: 0.7 !important;
    }
    
    .zento-message-attachment-file.zento-attachment-pending:hover {
      background: rgba(255, 255, 255, 0.15) !important;
    }
    
    .zento-message-attachment-file svg {
      width: 14px !important;
      height: 14px !important;
      flex-shrink: 0 !important;
    }
    
    .zento-message-attachment-file svg:last-child {
      width: 12px !important;
      height: 12px !important;
      opacity: 0.7 !important;
    }
    
    /* Error State */
    .zento-error {
      padding: 12px 16px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 12px;
      margin: 8px 0;
      display: flex;
      align-items: center;
      gap: 10px;
      animation: zento-slide-up 0.3s ease-out;
    }
    
    .zento-error-icon {
      width: 20px;
      height: 20px;
      fill: #ef4444;
      flex-shrink: 0;
    }
    
    .zento-error-text {
      font-size: 13px;
      color: #991b1b;
      flex: 1;
    }
    
    .zento-error-dismiss {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: background 0.2s;
    }
    
    .zento-error-dismiss:hover {
      background: #fee2e2;
    }
    
    .zento-error-dismiss svg {
      width: 16px;
      height: 16px;
      fill: #ef4444;
    }
    
    /* Debug Panel Styles */
    .zento-debug-toggle {
      margin-top: 8px;
    }
    
    .zento-debug-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      font-size: 11px;
      color: ${primaryColor};
      background: ${lighterColor};
      border: 1px solid ${primaryColor}30;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    .zento-debug-btn:hover {
      background: ${primaryColor}30;
    }
    
    .zento-debug-btn svg {
      width: 12px;
      height: 12px;
    }
    
    .zento-debug-panel {
      margin-top: 8px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
      animation: zento-slide-up 0.2s ease-out;
    }
    
    .zento-debug-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: linear-gradient(135deg, ${primaryColor}10 0%, ${primaryColor}05 100%);
      border-bottom: 1px solid #e2e8f0;
    }
    
    .zento-debug-title {
      font-size: 12px;
      font-weight: 600;
      color: #374151;
    }
    
    .zento-debug-close {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      font-size: 16px;
      color: #6b7280;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s;
    }
    
    .zento-debug-close:hover {
      background: #e5e7eb;
      color: #374151;
    }
    
    .zento-debug-content {
      padding: 8px;
    }
    
    .zento-debug-section {
      margin-bottom: 8px;
    }
    
    .zento-debug-section:last-child {
      margin-bottom: 0;
    }
    
    .zento-debug-subtitle {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      color: #6b7280;
      margin-bottom: 6px;
      letter-spacing: 0.5px;
    }
    
    .zento-debug-timing {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
    }
    
    .zento-timing-item {
      display: flex;
      justify-content: space-between;
      padding: 6px 8px;
      background: white;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
    }
    
    .zento-timing-label {
      font-size: 10px;
      color: #6b7280;
    }
    
    .zento-timing-value {
      font-size: 10px;
      font-weight: 600;
      color: ${primaryColor};
    }
    
    .zento-debug-layers {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }
    
    .zento-debug-layer {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 8px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      font-size: 10px;
    }
    
    .zento-layer-name {
      color: #374151;
      text-transform: capitalize;
    }
    
    .zento-layer-count {
      background: ${primaryColor};
      color: white;
      padding: 0 4px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 9px;
    }
    
    .zento-debug-files {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    
    .zento-debug-file {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 8px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
    }
    
    .zento-file-path {
      font-size: 10px;
      font-family: monospace;
      color: #374151;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 150px;
    }
    
    .zento-file-meta {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    
    .zento-file-layer {
      font-size: 9px;
      padding: 2px 6px;
      background: #e5e7eb;
      border-radius: 4px;
      color: #6b7280;
      text-transform: capitalize;
    }
    
    .zento-file-similarity {
      font-size: 9px;
      font-weight: 600;
      color: #059669;
    }
    
    .zento-debug-more {
      text-align: center;
      font-size: 10px;
      color: #6b7280;
      padding: 4px;
    }

    /* ========================================================================
       MARKDOWN STYLES - Full support for GFM-like rendering in widget
       ======================================================================== */
    
    /* Base text styling for message bubbles */
    .zento-message-bubble {
      color: #1e293b !important;
    }
    
    /* Headers */
    .zento-message-bubble h1.zento-md-h1,
    .zento-message-bubble .zento-md-h1 {
      display: block !important;
      font-size: 1.25em !important;
      font-weight: 700 !important;
      margin: 16px 0 10px 0 !important;
      padding: 0 0 8px 0 !important;
      color: #1e293b !important;
      border-bottom: 1px solid #e5e7eb !important;
      line-height: 1.3 !important;
    }
    
    .zento-message-bubble h1.zento-md-h1:first-child,
    .zento-message-bubble .zento-md-h1:first-child {
      margin-top: 0 !important;
    }
    
    .zento-message-bubble h2.zento-md-h2,
    .zento-message-bubble .zento-md-h2 {
      display: block !important;
      font-size: 1.15em !important;
      font-weight: 600 !important;
      margin: 14px 0 8px 0 !important;
      padding: 0 !important;
      color: #1e293b !important;
      line-height: 1.3 !important;
    }
    
    .zento-message-bubble h2.zento-md-h2:first-child,
    .zento-message-bubble .zento-md-h2:first-child {
      margin-top: 0 !important;
    }
    
    .zento-message-bubble h3.zento-md-h3,
    .zento-message-bubble .zento-md-h3 {
      display: block !important;
      font-size: 1.05em !important;
      font-weight: 600 !important;
      margin: 12px 0 6px 0 !important;
      padding: 0 !important;
      color: #334155 !important;
      line-height: 1.3 !important;
    }
    
    .zento-message-bubble h3.zento-md-h3:first-child,
    .zento-message-bubble .zento-md-h3:first-child {
      margin-top: 0 !important;
    }
    
    .zento-message-bubble h4.zento-md-h4,
    .zento-message-bubble h5.zento-md-h4,
    .zento-message-bubble h6.zento-md-h4,
    .zento-message-bubble .zento-md-h4 {
      display: block !important;
      font-size: 1em !important;
      font-weight: 600 !important;
      margin: 10px 0 5px 0 !important;
      padding: 0 !important;
      color: #475569 !important;
      line-height: 1.3 !important;
    }
    
    .zento-message-bubble h4.zento-md-h4:first-child,
    .zento-message-bubble .zento-md-h4:first-child {
      margin-top: 0 !important;
    }
    
    /* Paragraphs */
    .zento-message-bubble p.zento-md-p,
    .zento-message-bubble .zento-md-p {
      display: block !important;
      margin: 0 0 12px 0 !important;
      padding: 0 !important;
      line-height: 1.6 !important;
      color: inherit !important;
    }
    
    .zento-message-bubble p.zento-md-p:last-child,
    .zento-message-bubble .zento-md-p:last-child {
      margin-bottom: 0 !important;
    }
    
    /* Strong/Bold */
    .zento-message-bubble strong {
      font-weight: 600 !important;
      color: inherit !important;
    }
    
    /* Emphasis/Italic */
    .zento-message-bubble em {
      font-style: italic !important;
      color: inherit !important;
    }
    
    /* Strikethrough */
    .zento-message-bubble del {
      text-decoration: line-through !important;
      color: #64748b !important;
    }
    
    /* Lists - Unordered */
    .zento-message-bubble ul.zento-md-list.zento-md-ul,
    .zento-message-bubble ul.zento-md-ul {
      display: block !important;
      list-style-type: disc !important;
      margin: 12px 0 !important;
      padding: 0 0 0 24px !important;
    }
    
    /* Lists - Ordered */
    .zento-message-bubble ol.zento-md-list.zento-md-ol,
    .zento-message-bubble ol.zento-md-ol {
      display: block !important;
      list-style-type: decimal !important;
      margin: 12px 0 !important;
      padding: 0 0 0 24px !important;
    }
    
    /* List items */
    .zento-message-bubble .zento-md-list li,
    .zento-message-bubble ul.zento-md-ul li,
    .zento-message-bubble ol.zento-md-ol li {
      display: list-item !important;
      margin: 0 0 6px 0 !important;
      padding: 0 0 0 4px !important;
      line-height: 1.55 !important;
      color: inherit !important;
    }
    
    .zento-message-bubble .zento-md-list li:last-child,
    .zento-message-bubble ul.zento-md-ul li:last-child,
    .zento-message-bubble ol.zento-md-ol li:last-child {
      margin-bottom: 0 !important;
    }
    
    /* Nested lists */
    .zento-message-bubble .zento-md-list .zento-md-list,
    .zento-message-bubble ul.zento-md-ul ul.zento-md-ul,
    .zento-message-bubble ol.zento-md-ol ol.zento-md-ol {
      margin: 6px 0 !important;
    }
    
    .zento-message-bubble ul.zento-md-ul ul.zento-md-ul {
      list-style-type: circle !important;
    }
    
    .zento-message-bubble ul.zento-md-ul ul.zento-md-ul ul.zento-md-ul {
      list-style-type: square !important;
    }
    
    /* Line breaks */
    .zento-message-bubble br {
      display: block !important;
      content: "" !important;
      margin: 4px 0 !important;
    }
    
    /* Code blocks */
    .zento-message-bubble pre.zento-md-code-block,
    .zento-message-bubble .zento-md-code-block {
      display: block !important;
      background: #1f2937 !important;
      color: #f3f4f6 !important;
      padding: 14px 16px !important;
      border-radius: 10px !important;
      font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace !important;
      font-size: 12px !important;
      overflow-x: auto !important;
      margin: 12px 0 !important;
      white-space: pre-wrap !important;
      word-break: break-word !important;
      line-height: 1.5 !important;
    }
    
    .zento-message-bubble pre.zento-md-code-block code,
    .zento-message-bubble .zento-md-code-block code {
      background: transparent !important;
      color: inherit !important;
      padding: 0 !important;
      font-family: inherit !important;
      font-size: inherit !important;
    }
    
    /* Inline code */
    .zento-message-bubble code.zento-md-inline-code,
    .zento-message-bubble .zento-md-inline-code {
      display: inline !important;
      background: #f1f5f9 !important;
      color: #e11d48 !important;
      padding: 2px 6px !important;
      border-radius: 4px !important;
      font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace !important;
      font-size: 0.88em !important;
      white-space: nowrap !important;
    }
    
    /* Blockquotes */
    .zento-message-bubble blockquote.zento-md-blockquote,
    .zento-message-bubble .zento-md-blockquote {
      display: block !important;
      border-left: 4px solid ${primaryColor} !important;
      margin: 12px 0 !important;
      padding: 10px 16px !important;
      background: ${lighterColor} !important;
      border-radius: 0 8px 8px 0 !important;
      color: #4b5563 !important;
      font-style: italic !important;
    }
    
    .zento-message-bubble blockquote.zento-md-blockquote p,
    .zento-message-bubble .zento-md-blockquote p {
      margin: 0 !important;
    }
    
    /* Horizontal rules */
    .zento-message-bubble hr.zento-md-hr,
    .zento-message-bubble .zento-md-hr {
      display: block !important;
      border: none !important;
      border-top: 2px solid #e5e7eb !important;
      margin: 16px 0 !important;
      padding: 0 !important;
      height: 0 !important;
      width: 100% !important;
    }
    
    /* Links */
    .zento-message-bubble a.zento-md-link,
    .zento-message-bubble .zento-md-link {
      color: ${primaryColor} !important;
      text-decoration: none !important;
      cursor: pointer !important;
    }

    .zento-message.zento-user .zento-message-bubble a.zento-md-link,
    .zento-message.zento-user .zento-message-bubble .zento-md-link {
      color: white !important;
      text-decoration: underline !important;
      text-underline-offset: 2px !important;
    }
    
    .zento-message-bubble a.zento-md-link:hover,
    .zento-message-bubble .zento-md-link:hover {
      text-decoration: underline !important;
    }

    .zento-message.zento-user .zento-message-bubble a.zento-md-link:hover,
    .zento-message.zento-user .zento-message-bubble .zento-md-link:hover {
      color: rgba(255, 255, 255, 0.92) !important;
    }

    /* In-message ticket chip (opens the ticket PDF, like the Tickets tab) */
    .zento-message-bubble a.zento-message-ticket-link,
    .zento-message-bubble .zento-message-ticket-link {
      display: inline-flex !important;
      align-items: center !important;
      gap: 6px !important;
      margin-top: 8px !important;
      padding: 6px 12px !important;
      border-radius: 999px !important;
      background: rgba(255, 255, 255, 0.18) !important;
      border: 1px solid rgba(255, 255, 255, 0.35) !important;
      color: #ffffff !important;
      font-size: 12.5px !important;
      font-weight: 600 !important;
      line-height: 1 !important;
      text-decoration: none !important;
      cursor: pointer !important;
      transition: background 0.15s ease !important;
    }

    .zento-message-bubble a.zento-message-ticket-link:hover,
    .zento-message-bubble .zento-message-ticket-link:hover {
      background: rgba(255, 255, 255, 0.28) !important;
      text-decoration: none !important;
    }

    .zento-message-bubble .zento-message-ticket-link:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.85) !important;
      outline-offset: 2px !important;
    }

    .zento-message-ticket-link-icon {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 14px !important;
      height: 14px !important;
    }

    .zento-message-ticket-link-icon svg {
      width: 14px !important;
      height: 14px !important;
    }

    /* On white assistant bubbles fall back to a tinted chip for contrast */
    .zento-message.zento-assistant .zento-message-bubble .zento-message-ticket-link {
      background: ${primaryColor}1f !important;
      border-color: ${primaryColor}59 !important;
      color: ${primaryColor} !important;
    }

    .zento-message.zento-assistant .zento-message-bubble .zento-message-ticket-link:hover {
      background: ${primaryColor}33 !important;
    }
    
    /* Tables */
    .zento-message-bubble .zento-md-table-wrapper {
      display: block !important;
      overflow-x: auto !important;
      margin: 12px 0 !important;
      border-radius: 8px !important;
      border: 1px solid #e5e7eb !important;
    }
    
    .zento-message-bubble table.zento-md-table,
    .zento-message-bubble .zento-md-table {
      display: table !important;
      width: 100% !important;
      border-collapse: collapse !important;
      font-size: 13px !important;
    }
    
    .zento-message-bubble .zento-md-table thead {
      display: table-header-group !important;
    }
    
    .zento-message-bubble .zento-md-table tbody {
      display: table-row-group !important;
    }
    
    .zento-message-bubble .zento-md-table tr {
      display: table-row !important;
    }
    
    .zento-message-bubble th.zento-md-th,
    .zento-message-bubble .zento-md-th {
      display: table-cell !important;
      padding: 10px 14px !important;
      text-align: left !important;
      font-weight: 600 !important;
      background: #f9fafb !important;
      border-bottom: 2px solid #e5e7eb !important;
      color: #374151 !important;
      white-space: nowrap !important;
    }
    
    .zento-message-bubble td.zento-md-td,
    .zento-message-bubble .zento-md-td {
      display: table-cell !important;
      padding: 10px 14px !important;
      border-bottom: 1px solid #f3f4f6 !important;
      color: #4b5563 !important;
    }
    
    .zento-message-bubble .zento-md-table tr:last-child td.zento-md-td,
    .zento-message-bubble .zento-md-table tr:last-child .zento-md-td {
      border-bottom: none !important;
    }
    
    .zento-message-bubble .zento-md-table tr:hover {
      background: #f9fafb !important;
    }

    /* Mobile Responsive */
    @media (max-width: 480px) {
      .zento-widget-container {
        position: fixed !important;
        inset: auto !important;
        z-index: 2147483647 !important;
      }

      .zento-widget-container.zento-position-bottom-right {
        bottom: 20px !important;
        right: 20px !important;
      }

      .zento-widget-container.zento-position-bottom-left {
        bottom: 20px !important;
        left: 20px !important;
      }

      .zento-chat-window {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        width: 100dvw !important;
        height: 100vh !important;
        height: 100dvh !important;
        max-width: none !important;
        max-height: none !important;
        border-radius: 0 !important;
        transform: translateY(100%) !important;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s !important;
        display: flex !important;
        flex-direction: column !important;
      }

      .zento-chat-window.zento-visible {
        transform: translateY(0) !important;
        opacity: 1 !important;
      }

      .zento-chat-window.zento-closing {
        transform: translateY(100%) !important;
        opacity: 0 !important;
      }

      .zento-floating-button {
        position: fixed !important;
        bottom: 20px !important;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s !important;
        z-index: 2147483648 !important;
      }

      .zento-widget-container.zento-position-bottom-right .zento-floating-button {
        right: 20px !important;
      }

      .zento-widget-container.zento-position-bottom-left .zento-floating-button {
        left: 20px !important;
      }

      .zento-floating-button.zento-open {
        transform: scale(0.8) !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      .zento-header {
        padding: 10px 14px !important;
        padding-top: max(10px, env(safe-area-inset-top)) !important;
        border-radius: 0 !important;
        flex-shrink: 0 !important;
        gap: 10px !important;
      }

      .zento-header-avatar {
        width: 34px !important;
        height: 34px !important;
        min-width: 34px !important;
      }

      .zento-header-avatar-letter {
        font-size: 13px !important;
      }

      .zento-header-title {
        font-size: 14px !important;
      }

      .zento-messages-wrap {
        flex: 1 !important;
        min-height: 0 !important;
      }

      .zento-messages {
        padding: 12px !important;
        flex: 1 !important;
        min-height: 0 !important;
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch !important;
        gap: 8px !important;
      }

      .zento-jump-to-bottom {
        bottom: 10px !important;
        padding: 7px 14px !important;
        font-size: 12px !important;
      }

      .zento-message-avatar {
        width: 24px !important;
        height: 24px !important;
        min-width: 24px !important;
        margin-bottom: 14px !important;
      }

      .zento-message-avatar-letter {
        font-size: 9px !important;
      }

      .zento-message-bubble {
        font-size: 13.5px !important;
      }

      .zento-input-container {
        padding: 8px 14px !important;
        padding-bottom: max(12px, env(safe-area-inset-bottom)) !important;
        flex-shrink: 0 !important;
        background: white !important;
        position: relative !important;
        z-index: 10 !important;
      }

      /* Fix for keyboard open state */
      .zento-chat-window.zento-keyboard-open .zento-messages-wrap {
        min-height: 0 !important;
      }

      .zento-chat-window.zento-keyboard-open .zento-messages {
        padding-bottom: 8px !important;
      }

      .zento-chat-window.zento-keyboard-open .zento-input-container {
        padding-bottom: 12px !important;
      }

      .zento-conversation-list {
        border-radius: 0 !important;
        height: 100dvh !important;
      }

      .zento-conversation-list-header {
        padding: 16px !important;
        padding-top: max(16px, env(safe-area-inset-top)) !important;
      }

      .zento-conversation-avatar {
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
      }

      .zento-conversation-avatar-letter {
        font-size: 14px !important;
      }

      .zento-conversation-item {
        padding: 10px 14px !important;
      }

      .zento-debug-timing {
        grid-template-columns: 1fr !important;
      }

      .zento-welcome {
        padding: 24px 16px !important;
      }
    }

    /* Extra small devices */
    @media (max-width: 380px) {
      .zento-header {
        padding: 8px 12px !important;
        gap: 8px !important;
      }

      .zento-header-title {
        font-size: 13px !important;
      }

      .zento-input-wrapper {
        padding: 3px 3px 3px 12px !important;
      }

      .zento-send-button {
        width: 30px !important;
        height: 30px !important;
      }

      .zento-send-button svg {
        width: 14px !important;
        height: 14px !important;
      }
    }
  `;
};

/**
 * Utility to darken/lighten a hex color
 */
function shadeColor(color: string, percent: number): string {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}


